import React, { useState, useEffect, useRef } from 'react';
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { Toast } from 'primereact/toast';
import ReusableTable from "../../dashboard/ReusableTable";
import ReusableForm from "../../common/ReusableForm";
import ReusableDeleteDialog from '../../common/ReusableDeleteDialog';
import { getRequest, postRequest } from "../../services/apiService"; // Removed putRequest and deleteRequest

const EquipmentActivity = () => {
  const toast = useRef(null);
  const [activities, setActivities] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [currentActivity, setCurrentActivity] = useState(null);
  const [activityToDelete, setActivityToDelete] = useState(null);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [loading, setLoading] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    name: "",
    description: "",
  });

  // Delete confirmation form fields
  const deleteFormFields = [
    {
      name: "confirmation",
      label: "Confirmation",
      type: "text",
      required: true,
      placeholder: "Type 'DELETE' to confirm",
      helpText: "This action cannot be undone. Type 'DELETE' to confirm deletion."
    }
  ];

  // Delete form state
  const [deleteFormData, setDeleteFormData] = useState({
    confirmation: ""
  });

  const [search, setSearch] = useState("");
  const searchTimeout = useRef(null);

  // Fetch activities from API
  const fetchActivities = async () => {
    setLoading(true);
    try {
      const response = await getRequest(`master/equipment-activity/?page=${currentPage}`);
      setActivities(response.results?.data || []);
      setTotalRecords(response.count || 0);
    } catch (error) {
      console.error("Error fetching activities:", error);
      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: "Failed to fetch activities",
        life: 3000,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchActivities();
  }, [currentPage]);

  // Debounced search effect for table search
  useEffect(() => {
    if (searchTimeout.current) {
      clearTimeout(searchTimeout.current);
    }
    searchTimeout.current = setTimeout(() => {
      setLoading(true);
      getRequest(`master/equipment-activity/?search=${encodeURIComponent(search)}&page=${currentPage}`)
        .then(response => {
          setActivities(response.results?.data || []);
          setTotalRecords(response.count || 0);
        })
        .catch(() => {})
        .finally(() => setLoading(false));
    }, 700); // 700ms debounce
    return () => {
      if (searchTimeout.current) {
        clearTimeout(searchTimeout.current);
      }
    };
  }, [search, currentPage]);

  const handleTableSearch = (value) => {
    setSearch(value);
  };

  // Form fields configuration
  const activityFormFields = [
    {
      name: "name",
      label: "Activity Name",
      type: "text",
      required: true,
      placeholder: "Enter activity name",
      helpText: "Enter a unique name for the activity"
    },
    {
      name: "description",
      label: "Description",
      type: "textarea",
      required: false,
      placeholder: "Enter activity description",
      helpText: "Optional description of the activity"
    }
  ];

  // Handle form submission
  const handleSave = async () => {
    if (!formData.name) {
      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: "Please enter activity name",
        life: 3000,
      });
      return;
    }

    try {
      if (currentActivity) {
        // UPDATE: Use POST with id in payload
        await postRequest("master/equipment-activity/", {
          ...formData,
          id: currentActivity.id
        });
        toast.current.show({
          severity: "success",
          summary: "Success",
          detail: "Activity updated successfully!",
          life: 3000,
        });
      } else {
        // CREATE: Use POST with status_db
        await postRequest("master/equipment-activity/", {
          ...formData,
          status_db: 1
        });
        toast.current.show({
          severity: "success",
          summary: "Success",
          detail: "Activity created successfully!",
          life: 3000,
        });
      }
      
      // Refresh data
      fetchActivities();
      setShowAddModal(false);
      setShowEditModal(false);
      setFormData({ name: "", description: "" });
    } catch (error) {
      console.error("Error saving activity:", error);
      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: "Failed to save activity",
        life: 3000,
      });
    }
  };

  // Open add modal
  const openAddModal = () => {
    setCurrentActivity(null);
    setFormData({ name: "", description: "" });
    setShowAddModal(true);
  };

  // Open edit modal
  const openEditModal = (activity) => {
    setCurrentActivity(activity);
    setFormData({
      name: activity.name,
      description: activity.description || ""
    });
    setShowEditModal(true);
  };

  // Open delete confirmation
  const openDeleteModal = (activity) => {
    setActivityToDelete(activity);
    setDeleteFormData({ confirmation: "" });
    setShowDeleteModal(true);
  };

  const deleteActivity = async () => {
    if (deleteFormData.confirmation !== "DELETE") {
      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: "Please type 'DELETE' to confirm",
        life: 3000
      });
      return;
    }

    try {
      // DELETE: Use POST with status_db=3
      await postRequest("master/equipment-activity/", {
        id: activityToDelete.id,
        status_db: 3
      });
      
      toast.current.show({
        severity: "success",
        summary: "Success",
        detail: "Activity deleted successfully",
        life: 3000
      });
      setShowDeleteModal(false);
      setActivityToDelete(null);
      setDeleteFormData({ confirmation: "" });
      if (activities.length === 1 && currentPage > 1) {
        setCurrentPage(currentPage - 1);
      } else {
        fetchActivities();
      }
    } catch (error) {
      console.error("Error deleting activity:", error);
      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: "Failed to delete activity",
        life: 3000
      });
    }
  };

  // Handle pagination
  const onPageChange = (event) => {
    setCurrentPage(event.page + 1);
    setRowsPerPage(event.rows);
  };

  // Table columns
  const columnFields = [
    { field: "name", header: "Activity Name" },
    { field: "description", header: "Description" },
  ];

  // Action items for table
  const actionItems = [
    {
      label: "Edit",
      icon: "pi pi-pencil",
      command: (rowData) => openEditModal(rowData),
    },
    {
      label: "Delete",
      icon: "pi pi-trash",
      command: (rowData) => openDeleteModal(rowData),
    }
  ];

  return (
    <div className="min-h-screen bg-gray-100 py-4 px-4">
      <Toast ref={toast} />
      <div className="max-w-full mx-auto">
        {/* Table Section */}
        <div className="mt-0">
          <ReusableTable
            title="Equipment Activities"
            data={activities}
            setData={setActivities}
            columnFields={columnFields}
            setAddNewDialogVisible={openAddModal}
            actionMenu={true}
            actionItems={actionItems}
            loading={loading}
            totalRecords={totalRecords}
            rows={rowsPerPage}
            onPageChange={onPageChange}
            first={(currentPage - 1) * rowsPerPage}
            emptyMessage="No equipment activities found. Add new activities to get started."
            paginator
            paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
            currentPageReportTemplate="Showing {first} to {last} of {totalRecords} entries"
            rowsPerPageOptions={[5, 10, 25, 50]}
            className="p-datatable-sm"
            searchValue={search}
            onSearchChange={handleTableSearch}
          />
        </div>

        {/* Add/Edit Activity Modal using ReusableForm */}
        {(showAddModal || showEditModal) && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
            <div className="bg-white rounded-xl shadow-2xl w-full mx-4 animate-fade-in max-w-lg">
              <ReusableForm
                formData={formData}
                setFormData={setFormData}
                fields={activityFormFields}
                onSubmit={handleSave}
                onHide={() => {
                  setShowAddModal(false);
                  setShowEditModal(false);
                }}
                loading={loading}
                headerText={showEditModal ? "Edit Activity" : "Add New Activity"}
              />
            </div>
          </div>
        )}

        <ReusableDeleteDialog
          visible={showDeleteModal}
          onHide={() => setShowDeleteModal(false)}
          onDelete={deleteActivity}
          fields={deleteFormFields}
          formData={deleteFormData}
          setFormData={setDeleteFormData}
          loading={loading}
          title="Delete Activity"
          description={`Are you sure you want to delete activity "${activityToDelete?.name}"? This action cannot be undone. Type 'DELETE' to confirm.`}
          deleteLabel="Delete"
        />
      </div>
    </div>
  );
};

export default EquipmentActivity;